import { useState } from "react";
import { Clock, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

// Trial countdown + annual-upgrade nudge for current subscribers.
export default function UpgradeBanners() {
  const { t } = useI18n();
  const { sub, refreshSub } = useSubscription();
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!sub || !sub.is_pro) return null;
  const banners = [];

  if (sub.status === "trialing" && sub.current_period_end) {
    const days = Math.max(0, Math.ceil((sub.current_period_end * 1000 - Date.now()) / 86400000));
    banners.push(
      <div key="trial" data-testid="trial-banner"
        className="flex items-center gap-2 rounded-xl border border-white/10 bg-secondary/60 px-4 py-3">
        <Clock className="h-4 w-4 text-primary shrink-0" />
        <p className="text-sm">{days <= 0 ? t("lastTrialDay") : `${days} ${t("daysLeftTrial")}`}</p>
      </div>
    );
  }

  const switchYearly = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/switch-yearly");
      await Promise.all([refreshSub(), refresh()]);
      toast.success(t("switched"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  if (sub.plan === "vespera_pro_monthly" && sub.source === "personal") {
    banners.push(
      <div key="nudge" data-testid="annual-nudge" className="rounded-xl border border-primary/40 bg-primary/10 px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold">{t("switchYearlyTitle")}</p>
            <p className="text-xs text-muted-foreground">{t("switchYearlyDesc")}</p>
          </div>
        </div>
        <Button data-testid="switch-yearly-btn" onClick={switchYearly} disabled={busy}
          className="mt-2 w-full h-10 font-semibold">{t("switchYearlyCta")}</Button>
      </div>
    );
  }

  if (!banners.length) return null;
  return <div className="space-y-3 mb-4">{banners}</div>;
}
